import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CardList from "../ui/CardList.jsx";
import QueueTaskCard from "./queue/QueueTaskCard.jsx";
import TaskSheet from "./queue/TaskSheet.jsx";
import LaserMonitor from "./queue/LaserMonitor.jsx";
import { QUEUE_STATIONS, fetchQueueRows, findInQueue, rowStatus, stationBlocker, uniqueTasks } from "../../features/queue/queueData";
import { compareStationTasks, nextAllowedId, pendingMaintenanceFor } from "../../features/queue/queueRules";
import { parseQueueSearchNumber, rowMatchesNumber } from "../../utils/queueSearch";
import { parseScanCode, queueDeepLink, scanPath } from "../../utils/scanCodes";
import { readQueueStation, saveQueueStation } from "../queueStation";
import "../mobile.css";

// The phone Job Queue (plan Phase 1). A station picker; the station's In Progress, Paused,
// Maintenance and Pending tasks (in the desktop's order, "Next up" = the task the
// process-in-order lock allows); search by job / task number or a scanned traveler; a task opens
// as a full-screen sheet with its actions. The Laser is a monitor (cameras, E-stop, Pause/Resume).
// Layout only: reads come from features/queue/queueData, rules from queueRules, actions from
// taskActions.

const PENDING_SHOWN = 25;
const POLL_MS = 60000;


/** Plain-text search over a station's rows (job / task number, product, description, customer). */
function textMatches(row, q) {
  const hay = [row.master_job_num, row.task_id, row.product_id, row.product_description, row.job_description, row.customer_name, row.sub_process]
    .filter((v) => v != null)
    .map((v) => String(v).toLowerCase());
  return hay.some((h) => h.includes(q));
}

export default function QueueScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const [station, setStation] = useState(() => readQueueStation() || "Laser");
  const [rows, setRows] = useState([]);
  const [loadedFor, setLoadedFor] = useState(null); // the station `rows` belong to
  const [loadCount, setLoadCount] = useState(0); // bumps on every queue read (re-runs the lookup)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [lookup, setLookup] = useState(null); // {n, status: loading|done|unsupported|failed, rows}
  const [openRow, setOpenRow] = useState(null);
  const [showAllPending, setShowAllPending] = useState(false);
  const loadSeq = useRef(0);
  const lookupSeq = useRef(0);
  const pendingOpen = useRef(null); // a scanned task to open once its station's queue is in

  const load = useCallback(async (spinner = false) => {
    const seq = ++loadSeq.current;
    if (spinner) setLoading(true);
    try {
      const data = await fetchQueueRows(station);
      if (seq !== loadSeq.current) return;
      setRows(Array.isArray(data) ? data : []);
      setLoadedFor(station);
      setLoadCount((c) => c + 1);
      setError(null);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setError(err.message || "Couldn't load the queue.");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [station]);

  useEffect(() => {
    load(true);
    const t = setInterval(() => { if (!document.hidden) load(false); }, POLL_MS);
    const onVisible = () => { if (!document.hidden) load(false); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
      loadSeq.current += 1;
    };
  }, [load]);

  function changeStation(s) {
    if (s === station) return;
    saveQueueStation(s);
    setStation(s);
    setRows([]);
    setLoadedFor(null);
    setSearch("");
    setLookup(null);
    setShowAllPending(false);
    lookupSeq.current += 1;
  }

  // A scanned traveler (/s/task-N -> /queue?station=&task=&job=): that station, its number in the
  // search box (as the desktop does: the card, or "not in the queue", shows even if the sheet can't
  // open, and the order lock looks at that search, as on the desktop), then that task's sheet.
  useEffect(() => {
    const target = queueDeepLink(location.search);
    if (!target) return;
    navigate("/queue", { replace: true }); // a reload must not open it again
    pendingOpen.current = target;
    if (target.station !== station) changeStation(target.station);
    setSearch(String(target.taskId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const tasks = useMemo(() => {
    const list = station === "Laser" ? [...rows] : uniqueTasks(rows);
    return list.filter((r) => rowStatus(r) !== "Completed").sort(compareStationTasks(station));
  }, [rows, station]);

  const blocker = useMemo(() => stationBlocker(rows), [rows]);
  const maintenanceToday = useMemo(() => pendingMaintenanceFor(rows), [rows]);

  const q = search.trim().replace(/^\s*#\s*/, "").toLowerCase();
  const n = parseQueueSearchNumber(search);
  const results = useMemo(() => {
    if (!q) return null;
    const local = tasks.filter((r) => (n != null ? rowMatchesNumber(r, n) : textMatches(r, q)));
    if (local.length || !lookup || lookup.n !== n) return local;
    return lookup.rows;
  }, [q, n, tasks, lookup]);

  // "Process the top job first" looks at the list on screen, as the desktop's does (its filtered
  // list): a searched or scanned task is the top of its own list. The Laser's lock compares grouped
  // jobs on the desktop; the phone doesn't start Laser work.
  const nextAllowed = useMemo(
    () => (station === "Laser" ? null : nextAllowedId(results || tasks, station)),
    [results, tasks, station]
  );

  // A number the loaded queue doesn't hold is asked for by number (no date horizon), after a
  // short pause so typing "39044" is one request, not five; asked again after every queue read so
  // a found card's status stays as fresh as its neighbours' (the desktop does the same).
  const notLoaded = n != null && loadedFor === station && !tasks.some((r) => rowMatchesNumber(r, n));
  useEffect(() => {
    if (!notLoaded) return undefined;
    const seq = ++lookupSeq.current;
    setLookup((l) => (l && l.n === n ? l : { n, status: "loading", rows: [] }));
    const t = setTimeout(() => {
      findInQueue(station, n)
        .then(({ rows: found, unsupported }) => {
          if (seq !== lookupSeq.current) return;
          setLookup({ n, status: unsupported ? "unsupported" : "done", rows: uniqueTasks(found) });
        })
        .catch(() => {
          if (seq !== lookupSeq.current) return;
          setLookup({ n, status: "failed", rows: [] });
        });
    }, 350);
    return () => {
      clearTimeout(t);
      lookupSeq.current += 1;
    };
  }, [notLoaded, n, station, loadCount]);

  // The scanned task's sheet opens once its row is in (the station's queue, or the lookup).
  useEffect(() => {
    const t = pendingOpen.current;
    if (!t || t.station !== station || loadedFor !== station) return;
    const found = lookup && lookup.n === t.taskId ? lookup : null;
    const row = tasks.find((r) => Number(r.task_id) === t.taskId)
      || (found && found.rows.find((r) => Number(r.task_id) === t.taskId));
    if (row) {
      pendingOpen.current = null;
      setOpenRow(row);
    } else if (found && found.status !== "loading") {
      pendingOpen.current = null; // not found: the search results say so
    }
  }, [station, loadedFor, tasks, lookup]);

  // The open sheet always gets the newest copy of its row (status, timer fields).
  const liveOpenRow = openRow
    && (rows.find((r) => r.task_id === openRow.task_id)
      || (lookup && lookup.rows.find((r) => r.task_id === openRow.task_id))
      || openRow);

  // A scanner (or a pasted link) puts a whole traveler link in the box: let /s/ resolve it.
  function submitSearch(text) {
    const scanned = parseScanCode(text);
    if (scanned) {
      setSearch("");
      navigate(scanPath(scanned));
    }
  }

  const byStatus = (s) => tasks.filter((r) => rowStatus(r) === s);
  const card = (row) => <QueueTaskCard row={row} nextUp={row.task_id === nextAllowed} />;
  const listProps = { getKey: (r) => r.task_id, renderCard: card, onItemClick: setOpenRow };

  let searchState = null;
  if (results && results.length === 0) {
    if (lookup && lookup.n === n && lookup.status === "loading") searchState = "Searching all dates…";
    else if (lookup && lookup.n === n && lookup.status === "failed") searchState = `Couldn't search for #${n}. Check the connection and try again.`;
    else if (lookup && lookup.n === n && lookup.status === "unsupported") searchState = `Search beyond the loaded queue isn't available yet: #${n} could not be checked.`;
    else if (n != null && !(lookup && lookup.n === n)) searchState = "Searching all dates…";
    else searchState = n != null ? `#${n} is not in the ${station} queue.` : `Nothing in the ${station} queue matches "${search.trim()}".`;
  }

  const pending = byStatus("Pending");
  const sections = [
    ["In Progress", byStatus("In Progress")],
    ["Paused", byStatus("Paused")],
    ["Maintenance", byStatus("Maintenance")],
  ];

  return (
    <div className="jq-screen" data-testid="phone-queue">
      <div className="jq-stations" role="tablist" aria-label="Station">
        {QUEUE_STATIONS.map((s) => (
          <button key={s} type="button" role="tab" aria-selected={s === station} className={`jq-station${s === station ? " active" : ""}`} onClick={() => changeStation(s)}>
            {s}
          </button>
        ))}
      </div>

      {station === "Laser" && <LaserMonitor onChanged={() => load(false)} />}

      <div className="jq-list-tools">
        <form className="jq-search" role="search" onSubmit={(e) => { e.preventDefault(); submitSearch(search); }}>
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="search"
            enterKeyHint="search"
            className="form-control"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Job or task #, or scan a traveler"
            aria-label="Search this station's queue"
          />
        </form>
        <button type="button" className="btn btn-outline-secondary jq-filter-btn" onClick={() => load(true)} aria-label="Refresh the queue">
          <i className="fas fa-sync-alt" aria-hidden="true" />
        </button>
      </div>

      {blocker && <div className="jq-note jq-note-danger">🔒 Station locked: {blocker.maintenance_title || "Critical Maintenance"}</div>}
      {!blocker && maintenanceToday.length > 0 && (
        <div className="jq-note">Maintenance due today: {maintenanceToday.map((m) => m.title).join(", ")}</div>
      )}
      {error && <div className="jq-note jq-note-danger" role="alert">{error}</div>}

      {results ? (
        <section aria-label="Search results">
          <h2 className="jq-section-title">Results<span className="jq-queue-count">{results.length}</span></h2>
          {searchState ? <div className="jq-list-state">{searchState}</div> : <CardList items={results} {...listProps} testId="queue-results" />}
        </section>
      ) : (
        <>
          {loading && rows.length === 0 && (
            <div className="jq-list-state" role="status">
              <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" /> Loading the {station} queue…
            </div>
          )}
          {sections.map(([title, list]) => (list.length ? (
            <section key={title} aria-label={title}>
              <h2 className="jq-section-title">{title}<span className="jq-queue-count">{list.length}</span></h2>
              <CardList items={list} {...listProps} testId={`queue-${title.toLowerCase().replace(/\s+/g, "-")}`} />
            </section>
          ) : null))}
          {station !== "Laser" && (
            <section aria-label="Pending">
              <h2 className="jq-section-title">Pending<span className="jq-queue-count">{pending.length}</span></h2>
              <CardList
                items={showAllPending ? pending : pending.slice(0, PENDING_SHOWN)}
                {...listProps}
                loading={loading && rows.length === 0}
                empty={`Nothing pending at ${station}.`}
                testId="queue-pending"
              />
              {!showAllPending && pending.length > PENDING_SHOWN && (
                <button type="button" className="btn btn-outline-secondary jq-show-more" onClick={() => setShowAllPending(true)}>
                  Show all {pending.length}
                </button>
              )}
            </section>
          )}
          {station === "Laser" && !loading && sections.every(([, list]) => list.length === 0) && (
            <div className="jq-list-state">No job running on the Laser.</div>
          )}
        </>
      )}

      {liveOpenRow && (
        <TaskSheet
          key={liveOpenRow.task_id}
          row={liveOpenRow}
          station={station}
          stationLocked={Boolean(blocker)}
          nextAllowed={nextAllowed}
          pendingMaintenanceToday={maintenanceToday}
          onClose={() => setOpenRow(null)}
          onChanged={() => load(false)}
        />
      )}
    </div>
  );
}
